import { defineStore } from "pinia"
import type { VariacoesModeloEventoCalendario } from "@/store/atividades"
import { usarManipuladorObjetos } from "@/composable/UsarManipuladorObjetos/usarManipuladorObjetos"
import { ChaveValor, NoFilho } from "@/components/Gerais"
import { EntidadeGenerica } from "@/types/gerais"
import { NomeDosModulos } from "@/paginas/Atividades/CalendarioDeAtividades/CalendarioDeAtividades.modelos"
import { usarOcorrenciasDoCalendarioStore } from "../ocorrencias/ocorrencias.store"
import { AcumuladorDeFiltrosSelecionados, FiltrosDoCalendario, StateUsarFiltrosDoCalendario } from "./filtrosDoCalendario.modelos"
import { filtradorDeAtividades } from "./filtrosDoCalendario.script"
const { listaSemRepeticoes, ordenarLista } = usarManipuladorObjetos()

export const usarFiltrosDoCalendarioStore = defineStore("filtros-do-calendario", {
  state: (): StateUsarFiltrosDoCalendario => ({
    filtrosProfissionais: [],
    filtrosProjetos: [],
    filtrosTiposDeAtividade: [],
    filtrosSelecionados: [],
  }),
  getters: {
    arraysDeFiltrosEstaoVazios: (state: StateUsarFiltrosDoCalendario) => {
      return (
        state.filtrosProfissionais.length === 0 ||
        state.filtrosProjetos.length === 0 ||
        state.filtrosTiposDeAtividade.length === 0
      )
    },

    possuiFiltroSelecionado: (state: StateUsarFiltrosDoCalendario) => state.filtrosSelecionados.length > 0,

    acumuladorDeIdsFiltrosSelecionados: (state: StateUsarFiltrosDoCalendario): AcumuladorDeFiltrosSelecionados => {
      return state.filtrosSelecionados.reduce((acumulador: AcumuladorDeFiltrosSelecionados, item: ChaveValor<string>) => {
        acumulador[item.chave].push(item.valor)
        return acumulador
      }, {
        profissional: [],
        tiposDeAtividade: [],
        projeto: []
      })
    },
  },
  actions: {
    async extrairFiltrosAPartirDeEventos(eventos: VariacoesModeloEventoCalendario[]) {
      const [profissionais, tiposDeAtividade, projeto] = eventos.reduce((acumulador: Array<EntidadeGenerica[]>, item) => {
        acumulador[0].push(...item.profissionais)
        acumulador[1].push(item.tipoDeAtividade)
        acumulador[2].push(item.projeto as EntidadeGenerica)
        return acumulador
      }, [[], [], []]).map(item => [...item])

      const prepararDadosParaFiltrar = async (dados: EntidadeGenerica[], chave: string): Promise<FiltrosDoCalendario[]> => {
        return new Promise(resolve => {
          const dadosSemRepeticao = listaSemRepeticoes(dados.filter(dado => dado), 'id')
          ordenarLista(dadosSemRepeticao, 'nome')
          const dadosMapeados = mapearDadosDeFiltroDoEvento(dadosSemRepeticao, chave)
          resolve(dadosMapeados)
        })
      }

      const [profissionaisMapeados, tiposDeAtividadeMapeados, projetosMapeados] = await Promise.all([
        prepararDadosParaFiltrar(profissionais, 'profissional'),
        prepararDadosParaFiltrar(tiposDeAtividade, 'tiposDeAtividade'),
        prepararDadosParaFiltrar(projeto, 'projeto')
      ])

      const buscarSelecionadosQueNaoEstaoNosNovosFiltros = async (selecionados: ChaveValor<NomeDosModulos>[], listaAtual: FiltrosDoCalendario[], novaLista: FiltrosDoCalendario[]): Promise<FiltrosDoCalendario[]> => {
        return new Promise((resolve) => {
          const itensQueVaoPersistirNaLista = selecionados.filter(selecionado => !novaLista.some(novoItem => novoItem.valor === selecionado.valor))
          const resultado = listaAtual.filter(item => itensQueVaoPersistirNaLista.some(itemQueVaiPersistir => itemQueVaiPersistir.valor === item.valor))
          resolve(resultado)
        })
      }


      if (this.possuiFiltroSelecionado) {
        const selecionados = [... this.filtrosSelecionados]
        const [profissionaisPersistentes, tiposDeAtividadePersistentes, projetosPersistentes] = await Promise.all([
          buscarSelecionadosQueNaoEstaoNosNovosFiltros(selecionados, this.filtrosProfissionais, profissionaisMapeados),
          buscarSelecionadosQueNaoEstaoNosNovosFiltros(selecionados, this.filtrosTiposDeAtividade, tiposDeAtividadeMapeados),
          buscarSelecionadosQueNaoEstaoNosNovosFiltros(selecionados, this.filtrosProjetos, projetosMapeados)
        ])
        this.filtrosProfissionais = [...profissionaisMapeados, ...profissionaisPersistentes]
        this.filtrosTiposDeAtividade = [...tiposDeAtividadeMapeados, ...tiposDeAtividadePersistentes]
        this.filtrosProjetos = [...projetosMapeados, ...projetosPersistentes]
      } else {
        this.filtrosProfissionais = profissionaisMapeados
        this.filtrosTiposDeAtividade = tiposDeAtividadeMapeados
        this.filtrosProjetos = projetosMapeados
      }
    },

    async filtrarDadosDoCalendario(textoDaBusca: string) {
      const filtrarItens = async (itens: NoFilho[]): Promise<NoFilho[]> => {
        return new Promise((resolve) => {
          const itensFiltrados = itens.map((itemDoFiltro) => {
            const existeComBaseNosFiltros = itemDoFiltro.key.includes(textoDaBuscaTratado)
            return {
              ...itemDoFiltro,
              contemNoFiltro: existeComBaseNosFiltros,
            }
          })
          resolve(itensFiltrados)
        })
      }

      const copiaDeProjetos = [...this.filtrosProjetos]
      const copiaDeTiposDeAtividade = [...this.filtrosTiposDeAtividade]
      const copiaDeProfissionais = [...this.filtrosProfissionais]

      if (textoDaBusca.length < 3) {
        copiaDeProjetos.forEach(projeto => projeto.contemNoFiltro = true)
        copiaDeTiposDeAtividade.forEach(tipoDeAtividade => tipoDeAtividade.contemNoFiltro = true)
        copiaDeProfissionais.forEach(profissional => profissional.contemNoFiltro = true)

        this.filtrosProjetos = copiaDeProjetos
        this.filtrosTiposDeAtividade = copiaDeTiposDeAtividade
        this.filtrosProfissionais = copiaDeProfissionais
        return
      }

      const textoDaBuscaTratado =
        transformarTextoEmMinusculoESemAcento(textoDaBusca)

      await Promise.all([
        filtrarItens(copiaDeProjetos),
        filtrarItens(copiaDeTiposDeAtividade),
        filtrarItens(copiaDeProfissionais)
      ]).then(([projetosFiltrados, tiposDeAtividadeFiltrados, profissionaisFiltrados]) => {
        this.filtrosProjetos = projetosFiltrados
        this.filtrosTiposDeAtividade = tiposDeAtividadeFiltrados
        this.filtrosProfissionais = profissionaisFiltrados
      })
    },

    async filtrarCalendario(todasAtividadesDoCalendario: VariacoesModeloEventoCalendario[]) {
      const { ocorrencias } = usarOcorrenciasDoCalendarioStore()

      const filtrarPorOcorrencias = (): boolean => {
        return Object.values(ocorrencias).some((ocorrencia) => ocorrencia)
      }

      if (!this.filtrosSelecionados.length && !filtrarPorOcorrencias()) {
        return [...todasAtividadesDoCalendario]
      }

      return filtradorDeAtividades([...todasAtividadesDoCalendario], this.acumuladorDeIdsFiltrosSelecionados, ocorrencias)
    },

    limparListaDeFiltros() {
      this.filtrosProfissionais = []
      this.filtrosTiposDeAtividade = []
      this.filtrosProjetos = []
    },

    limparFiltrosSelecionados() {
      this.filtrosSelecionados = []
    }
  }
})

const mapearDadosDeFiltroDoEvento = (dados: EntidadeGenerica[], chave: string): FiltrosDoCalendario[] => {
  return dados.map((dado) => {
    return {
      key: transformarTextoEmMinusculoESemAcento(dado.nome),
      valor: dado.id,
      chave: chave,
      rotulo: dado.nome,
      type: "filho",
      contemNoFiltro: true,
    }
  })
}

const transformarTextoEmMinusculo = (texto: string) => {
  return texto.toLowerCase()
}

const removerAcentosDoTexto = (texto: string) => {
  const acentos = {
    a: /[áàãâä]/g,
    e: /[éèêë]/g,
    i: /[íìîïí]/g,
    o: /[óòõôö]/g,
    u: /[úùûü]/g,
    c: /[ç]/g,
  }

  for (const letra in acentos) {
    if (Object.prototype.hasOwnProperty.call(acentos, letra)) {
      texto = texto.replace(acentos[letra], letra)
    }
  }
  return texto
}

const transformarTextoEmMinusculoESemAcento = (texto: string) => {
  const textoMinusculo = transformarTextoEmMinusculo(texto)
  return removerAcentosDoTexto(textoMinusculo)
}
